import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Coins, CreditCard, FileSearch, KeyRound, Landmark, LogIn, Settings2, ShieldAlert, UserCog, UserPlus, X } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { adminApi } from '@/lib/api/endpoints';
import { isApiError, userMessage } from '@/lib/api/errors';
import type { Activity, Purchase } from '@/lib/api/types';
import { ME_KEY } from '@/lib/auth';
import { cn, formatDateTime, formatMoney, timeAgo } from '@/lib/utils';

function useInvalidateAdmin() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['admin'] });
    qc.invalidateQueries({ queryKey: ME_KEY });
  };
}

/** Approve / Decline buttons with their dialogs. Only rendered for pending purchases. */
export function ReviewActions({ purchase }: { purchase: Purchase }) {
  const [mode, setMode] = React.useState<'approve' | 'decline' | null>(null);
  const [coins, setCoins] = React.useState(String(purchase.coins_requested));
  const [note, setNote] = React.useState('');
  const invalidate = useInvalidateAdmin();

  const onError = (err: unknown) => {
    toast.error(userMessage(err, 'Failed'));
    if (isApiError(err, 409)) {
      setMode(null);
      invalidate();
    }
  };

  const approve = useMutation({
    mutationFn: () => {
      const n = Number(coins);
      return adminApi.approve(purchase.id, {
        coins: n !== purchase.coins_requested ? n : undefined,
        note: note.trim() || undefined,
      });
    },
    onSuccess: (p) => {
      toast.success(`Approved: ${p.coins_granted} coins to @${p.username}`);
      setMode(null);
      invalidate();
    },
    onError,
  });
  const decline = useMutation({
    mutationFn: () => adminApi.decline(purchase.id, note.trim()),
    onSuccess: (p) => {
      toast.success(`Declined @${p.username}'s purchase`);
      setMode(null);
      invalidate();
    },
    onError,
  });

  if (purchase.status !== 'pending') return null;
  const coinsN = Number(coins);
  const coinsValid = Number.isInteger(coinsN) && coinsN > 0;

  return (
    <>
      <Button size="sm" variant="success" onClick={() => { setNote(''); setMode('approve'); }}>
        <Check /> Approve
      </Button>
      <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => { setNote(''); setMode('decline'); }}>
        <X /> Decline
      </Button>

      <Dialog open={mode !== null} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{mode === 'approve' ? 'Approve purchase' : 'Decline purchase'}</DialogTitle>
            <DialogDescription>
              @{purchase.username} says they paid {formatMoney(purchase.amount, purchase.currency)} to {purchase.bank_account.bank_name} for {purchase.coins_requested} coins.
            </DialogDescription>
          </DialogHeader>
          {mode === 'approve' ? (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (coinsValid) approve.mutate();
              }}
            >
              <Field label="Coins to credit" htmlFor="coins" hint="Change only if the transfer was a different amount." error={coinsValid ? undefined : 'Enter a whole number above 0.'}>
                <Input id="coins" type="number" min={1} icon={<Coins />} value={coins} onChange={(e) => setCoins(e.target.value)} />
              </Field>
              <Field label="Note (optional)" htmlFor="approve-note">
                <Input id="approve-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. paid 400" />
              </Field>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setMode(null)}>Cancel</Button>
                <Button type="submit" variant="success" loading={approve.isPending} disabled={!coinsValid}>
                  Approve and credit {coinsValid ? coinsN : ''} coins
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (note.trim()) decline.mutate();
              }}
            >
              <Field label="Reason (the customer sees this)" htmlFor="reason">
                <Textarea id="reason" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Receipt unreadable" autoFocus />
              </Field>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setMode(null)}>Cancel</Button>
                <Button type="submit" variant="destructive" loading={decline.isPending} disabled={!note.trim()}>
                  Decline
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const ACTION_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  auth: LogIn,
  purchase: CreditCard,
  check: FileSearch,
  settings: Settings2,
  bank_account: Landmark,
  user: UserCog,
  coins: Coins,
};

const ACTION_TEXT: Record<string, string> = {
  'check.cancel': 'stopped a check',
  'auth.register': 'signed up',
  'auth.login': 'signed in',
  'auth.login_failed': 'failed to sign in',
  'purchase.create': 'sent a payment receipt',
  'purchase.approve': 'approved a purchase',
  'purchase.decline': 'declined a purchase',
  'check.create': 'started a check',
  'check.retry': 'retried a check',
  'check.screenshot_upload': 'sent a portal screenshot',
  'check.screenshot_approve': 'approved a portal screenshot',
  'check.screenshot_decline': 'declined a portal screenshot',
  'settings.update': 'changed the pricing rules',
  'bank_account.create': 'added a bank account',
  'bank_account.update': 'updated a bank account',
  'user.update': 'updated a user',
  'coins.adjust': 'adjusted coins',
};

function detailSummary(a: Activity): string {
  const d = a.details ?? {};
  const parts: string[] = [];
  if (typeof d.coins === 'number') parts.push(`${d.coins} coins`);
  if (typeof d.amount === 'string') parts.push(d.amount);
  if (typeof d.delta === 'number') parts.push(`${d.delta > 0 ? '+' : ''}${d.delta} coins`);
  if (typeof d.reason === 'string') parts.push(`“${d.reason}”`);
  if (typeof d.note === 'string') parts.push(`“${d.note}”`);
  if (typeof d.username === 'string' && a.action === 'auth.login_failed') parts.push(`as ${d.username}`);
  if (typeof d.signup_bonus === 'number') parts.push(`bonus ${d.signup_bonus}`);
  if (typeof d.file_status === 'string') parts.push(d.file_status);
  return parts.join(' · ');
}

export function ActivityRow({ a }: { a: Activity }) {
  const [open, setOpen] = React.useState(false);
  const Icon = ACTION_ICON[a.action.split('.')[0] ?? ''] ?? KeyRound;
  const failed = a.action === 'auth.login_failed';
  const summary = detailSummary(a);
  const checkId = typeof a.details?.check_id === 'string' ? a.details.check_id : null;
  return (
    <div className="px-4 py-3 sm:px-5">
      <div className="flex items-start gap-3">
        <span className={cn('mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg', failed ? 'bg-rose-500/10 text-rose-600' : 'bg-secondary text-muted-foreground')}>
          {failed ? <ShieldAlert className="size-4" /> : a.action === 'auth.register' ? <UserPlus className="size-4" /> : <Icon className="size-4" />}
        </span>
        <div className="min-w-0 flex-1 text-sm">
          <p>
            {a.user_id ? (
              <Link to={`/admin/users/${a.user_id}`} className="font-semibold hover:underline">
                @{a.username ?? 'unknown'}
              </Link>
            ) : (
              <span className="font-semibold">Someone</span>
            )}{' '}
            <span className="text-muted-foreground">{ACTION_TEXT[a.action] ?? a.action}</span>
            {summary && <span className="text-muted-foreground"> · {summary}</span>}
            {checkId && (
              <Link to={`/admin/checks/${checkId}`} className="ml-1 text-primary-ink hover:underline">
                view
              </Link>
            )}
          </p>
          <p className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-muted-foreground">
            <span title={formatDateTime(a.created_at)}>{timeAgo(a.created_at)}</span>
            {a.ip && <span className="font-mono">{a.ip}</span>}
            <span className="font-mono">{a.action}</span>
            {a.details && (
              <button type="button" className="text-primary-ink hover:underline" onClick={() => setOpen((o) => !o)}>
                {open ? 'hide' : 'details'}
              </button>
            )}
          </p>
          {open && a.details && (
            <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-secondary/60 p-3 font-mono text-xs">{JSON.stringify(a.details, null, 2)}</pre>
          )}
        </div>
      </div>
    </div>
  );
}

export { useInvalidateAdmin };
